import { CancelToken } from "axios";
import api from "@/services/api.service";
import { namespace, State } from "vuex-class";
import { Component, Prop, Vue } from "vue-property-decorator";

import { Response } from "@/store/responses/types";
import { MapLayerItem, MapLayerItemTypeIds, MapLayerItemDeviceOption } from "@/store/map-layers/types";

const MapLayers = namespace("mapLayers");

/**
 * @summary Mixin for shared methods in Map Setup.
 */
@Component
export default class MapSetupMixin extends Vue {
    @Prop({ default: null }) public mapLayerItem: MapLayerItem; // The Item we are Creating / Editing.

    @MapLayers.State("hidePlotted") private hidePlotted: boolean;
    @MapLayers.Mutation("setHidePlotted") private setHidePlotted: (hidePlotted: boolean) => void;

    public isEditMode: boolean = false;
    public availableDevices: MapLayerItemDeviceOption[] = [];

    /**
     * @summary Translates the mapLayerItemTypeId into a user friendly title.
    */
    public typeDictionary: Map<number, string> = new Map([
        [1, "Camera"],
        [2, "Audio"],
        [3, "Door"],
        [4, "Alarm"],
        [5, "Output"],
        [6, "Geofence"]
    ]);

    /**
     * @summary Translates the mapLayerItemTypeId into a user friendly title (plural).
    */
    public typeDictionaryPluralized: Map<number, string> = new Map([
        [1, "Cameras"],
        [2, "Audio"],
        [3, "Doors"],
        [4, "Alarms"],
        [5, "Outputs"],
        [6, "Geofences"]
    ]);

    /**
     * @summary The updated map layer item.
     */
    public updatedMapLayerItem: MapLayerItem = {
        mapLayerItemId: 0,
        mapLayerItemTypeId: 0,
        objectId: 0,
        extraValue: "",
        title: "",
        latLong: "",
        minElevation: 0,
        maxElevation: 0,
        regionPath: "",
        mapLayerIds: [],
        hideOnMap: false,
        groupId: null,
        lastInteractionHealthy: null
    };

    /**
     * @summary Returns popup edit mode state.
     */
    public get isEditModeEnabled() {
        return this.isEditMode;
    }

    private get hidePlottedItems(): boolean {
        return this.hidePlotted;
    }

    private set hidePlottedItems(value: boolean) {
        this.setHidePlotted(value);
    }

    /**
     * @summary load the appropriate devices depending on the item type.
     */
    public async loadDevices(params?: { filter?: string, groupId?: number; cancelToken?: CancelToken}): Promise<void> {
        const { filter = null, groupId = null, cancelToken = null } = params ?? {};

        this.clearAvailableDevices();

        // Fetch responses:
        if (this.updatedMapLayerItem.mapLayerItemTypeId == MapLayerItemTypeIds.Alarm) {
            let responses: Response[] = await api.getAllResponsesByArea(
                groupId,
                filter,
                false,
                this.hidePlottedItems,
                cancelToken
            );

            // Convert to SelectionObject
            responses.forEach(response => {
                const availableDevicesContainsResponse = this.availableDevices.find(
                    (d: MapLayerItemDeviceOption) => d.title == response.title && d.objectId == response.responseID);
                if (!availableDevicesContainsResponse) {
                    this.availableDevices.push(new MapLayerItemDeviceOption(response, this.updatedMapLayerItem.mapLayerItemTypeId));
                }
            });
        }
        // Fetch devices:
        else {
            const _groupId: number = groupId ? groupId : -1;
            let devices = await api.getDevicesByMapLayerItemType(
                _groupId,
                this.mapLayerItem.mapLayerItemTypeId,
                filter,
                this.hidePlottedItems,
                cancelToken
            );

            // Convert to SelectionObject
            devices.forEach(device => {
                const availableDevicesContainsDevice = this.availableDevices.find(
                    (d: MapLayerItemDeviceOption) => d.title == device.title && d.objectId == device.deviceID);
                if (!availableDevicesContainsDevice) {
                    this.availableDevices.push(new MapLayerItemDeviceOption(device, this.updatedMapLayerItem.mapLayerItemTypeId));
                }
            });
        }

        this.sortAvailableDevices();
    }

    /**
     * @summary Clears the array of available devices in order to remove plotted items if hidePlotted is true.
     */
    private clearAvailableDevices(): void {
        if (this.hidePlottedItems) {
            this.availableDevices.splice(0, this.availableDevices.length); // Clear the array of available devices.
        }
    }

    /**
     * @summary Sorts the available devices by title.
     */
    private sortAvailableDevices(): void {
        this.availableDevices.sort((a, b) => {
            if (a.title < b.title) {
                return -1;
            }
            if (a.title > b.title) {
                return 1;
            }
            return 0;
        });
    }
}